import { useState, useRef, useEffect } from 'react';
import { mockBeats } from '../../../mocks/beats';
import AnimatedSection from '../../../components/base/AnimatedSection';
import ShareBeatPopup from './ShareBeatPopup';

interface Beat {
  id: string;
  title: string;
  price: number;
  licenseType: string;
  isFree: boolean;
  sold: boolean;
  bpm: number;
  key: string;
  genre: string[];
  audioUrl: string;
  downloadUrl?: string;
  waveformImage: string;
}

interface FeaturedBeatsProps {
  onPlayBeat: (beat: Beat) => void;
  currentBeat: Beat | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  onSeek: (time: number) => void;
  onVolumeChange: (vol: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onAddToCart: (beat: Beat) => void;
  onRemoveFromCart: (id: string) => void;
  cartItemIds: Set<string>;
  activeGenre: string;
  onGenreChange: (genre: string) => void;
  filteredBeats: Beat[];
}

export default function FeaturedBeats({
  onPlayBeat,
  currentBeat,
  isPlaying,
  currentTime,
  duration,
  volume,
  onSeek,
  onVolumeChange,
  onPrev,
  onNext,
  onAddToCart,
  onRemoveFromCart,
  cartItemIds,
  activeGenre,
  onGenreChange,
  filteredBeats,
}: FeaturedBeatsProps) {
  const [isShuffle, setIsShuffle] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [freeBeat, setFreeBeat] = useState<Beat | null>(null);
  const [hasFollowed, setHasFollowed] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [sharePopupBeatId, setSharePopupBeatId] = useState<string | null>(null);
  const trackListRef = useRef<HTMLDivElement>(null);

  const genres = ['All', 'Afrobeats', 'Trap', 'Rap', 'Drill', 'Reggaeton', 'R&B', 'Pop', 'Dembow', 'Hip-Hop', 'Merengue', 'EDM'];

  const activeBeat = currentBeat ?? mockBeats[0];

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleFreeDownload = (beat: Beat) => {
    setFreeBeat(beat);
    setHasFollowed(false);
    setShowInstagramModal(true);
  };

  const handleFollowClick = () => {
    setHasFollowed(true);
    window.open('https://www.instagram.com/officialjaymaximus/', '_blank', 'noopener,noreferrer');
  };

  const handleDownloadNow = () => {
    if (!freeBeat?.downloadUrl) return;
    const a = document.createElement('a');
    a.href = freeBeat.downloadUrl;
    a.download = `${freeBeat.title}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setShowInstagramModal(false);
    setHasFollowed(false);
  };

  const handleContactSimilar = (beat: Beat, e: React.MouseEvent) => {
    e.stopPropagation();
    const bookingEl = document.getElementById('booking');
    if (bookingEl) {
      bookingEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    const el = trackListRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setIsAtBottom(nearBottom);
  };

  const scrollDown = () => {
    trackListRef.current?.scrollBy({ top: 320, behavior: 'smooth' });
  };

  return (
    <section id="beats" className="py-28 bg-white">
      {/* Instagram Follow Modal */}
      {showInstagramModal && freeBeat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
              <i className="ri-instagram-line text-white text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Get This Beat Free!</h3>
            <p className="text-gray-500 text-sm mb-1">
              <span className="font-semibold text-gray-800">{freeBeat.title}</span> is on us — no charge.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Follow our Instagram page to unlock your free download.
            </p>
            {!hasFollowed ? (
              <>
                <button
                  onClick={handleFollowClick}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap mb-3"
                >
                  <i className="ri-instagram-line text-lg"></i>
                  Follow on Instagram
                </button>
                <button
                  onClick={() => { setShowInstagramModal(false); setHasFollowed(false); }}
                  className="w-full py-2.5 rounded-xl text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Maybe Later
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 mb-4 text-green-600 text-sm font-semibold">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  Thanks for following! Your download is ready.
                </div>
                <button
                  onClick={handleDownloadNow}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-white bg-gray-900 hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap mb-3"
                >
                  <i className="ri-download-2-line text-lg"></i>
                  Download Now
                </button>
                <button
                  onClick={() => { setShowInstagramModal(false); setHasFollowed(false); }}
                  className="w-full py-2.5 rounded-xl text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <AnimatedSection animation="fade-up" className="mb-8 md:mb-12">
          <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full text-xs font-semibold text-gray-600 tracking-widest mb-3 uppercase">
            New Releases
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-2 leading-tight">
            Latest Fire Beats<br />Ready for Purchase.
          </h2>
          <p className="text-sm md:text-base text-gray-500">Exclusive rights — own it fully. Professional quality production ready for your next project.</p>
        </AnimatedSection>

        <div className="flex flex-col md:flex-row gap-5 md:gap-8 items-start">
          {/* Left: Player Panel */}
          <AnimatedSection animation="slide-left" delay={200} className="w-full md:w-[352px] md:flex-shrink-0 bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 shadow-sm">
            {/* Album Art */}
            <div className="w-full aspect-square rounded-xl overflow-hidden mb-5 shadow-md relative">
              <img
                src={activeBeat.waveformImage}
                alt={activeBeat.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute top-2 left-2 px-2 py-0.5 bg-gray-900 text-white text-xs font-bold rounded-full tracking-wide uppercase">
                Exclusive
              </div>
              {activeBeat.isFree && (
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full tracking-wide uppercase">
                  Free
                </div>
              )}
              {activeBeat.sold && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <span className="px-4 py-1.5 bg-red-600 text-white text-sm font-black rounded-full tracking-widest uppercase rotate-[-8deg] shadow-lg">
                    SOLD
                  </span>
                </div>
              )}
            </div>

            {/* Track Info */}
            <div className="mb-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-bold text-gray-900 truncate flex-1">{activeBeat.title}</h3>
                <div className="relative flex-shrink-0">
                  <button
                    onClick={e => { e.stopPropagation(); setSharePopupBeatId(sharePopupBeatId === activeBeat.id + '-panel' ? null : activeBeat.id + '-panel'); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                    title="Share this beat"
                  >
                    <i className="ri-share-forward-line text-base"></i>
                  </button>
                  {sharePopupBeatId === activeBeat.id + '-panel' && (
                    <ShareBeatPopup
                      beat={activeBeat}
                      onClose={() => setSharePopupBeatId(null)}
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {activeBeat.genre.map(g => (
                  <span key={g} className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">{g}</span>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <i className="ri-speed-line text-gray-400"></i>
                  {activeBeat.bpm} BPM
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-music-2-line text-gray-400"></i>
                  {activeBeat.key}
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="relative w-full h-1.5 bg-gray-200 rounded-full cursor-pointer">
                <div
                  className="absolute left-0 top-0 h-full bg-red-600 rounded-full transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={e => onSeek(parseFloat(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between mb-5">
              <button
                onClick={() => setIsShuffle(!isShuffle)}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${isShuffle ? 'text-red-600' : 'text-gray-400 hover:text-gray-700'}`}
              >
                <i className="ri-shuffle-line text-lg"></i>
              </button>
              <button
                onClick={onPrev}
                className="w-8 h-8 flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
              >
                <i className="ri-skip-back-fill text-xl"></i>
              </button>
              <button
                onClick={() => onPlayBeat(activeBeat)}
                className="w-[52px] h-[52px] flex items-center justify-center bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-md cursor-pointer"
              >
                {isPlaying && currentBeat?.id === activeBeat.id ? (
                  <i className="ri-pause-fill text-xl"></i>
                ) : (
                  <i className="ri-play-fill text-xl ml-0.5"></i>
                )}
              </button>
              <button
                onClick={onNext}
                className="w-8 h-8 flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
              >
                <i className="ri-skip-forward-fill text-xl"></i>
              </button>
              <button
                onClick={() => setIsRepeat(!isRepeat)}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${isRepeat ? 'text-red-600' : 'text-gray-400 hover:text-gray-700'}`}
              >
                <i className="ri-repeat-line text-lg"></i>
              </button>
            </div>

            {/* Volume */}
            <div className="flex items-center gap-2 mb-5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-volume-up-line text-gray-400 text-sm"></i>
              </div>
              <div className="relative flex-1 h-1.5 bg-gray-200 rounded-full">
                <div
                  className="absolute left-0 top-0 h-full bg-gray-400 rounded-full"
                  style={{ width: `${volume * 100}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={e => onVolumeChange(parseFloat(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>

            {/* If Sold — Inquiry Note */}
            <div className="mb-4 flex items-start gap-2 px-3 py-2.5 bg-gray-100 rounded-xl border border-gray-200">
              <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-information-line text-gray-400 text-sm"></i>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                <span className="font-semibold text-gray-700">Beat already sold?</span>{' '}
                <a
                  href="#booking"
                  className="text-red-600 hover:text-red-700 underline underline-offset-2 font-medium cursor-pointer"
                >
                  Inquire about a similar style
                </a>{' '}
                — we can produce something just for you.
              </p>
            </div>

            {/* Status / Add to Cart */}
            {activeBeat.isFree ? (
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-green-600">FREE</span>
                <button
                  onClick={() => handleFreeDownload(activeBeat)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-pink-500 to-red-600 text-white rounded-xl hover:opacity-90 transition-opacity font-semibold text-sm cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-instagram-line"></i>
                  Get Free
                </button>
              </div>
            ) : activeBeat.sold ? (
              <div className="flex flex-col gap-2">
                <span className="flex items-center justify-center gap-2 py-2 bg-gray-100 text-gray-500 rounded-xl font-bold text-xs tracking-widest uppercase border border-gray-200">
                  <i className="ri-lock-2-line"></i>
                  Purchased
                </span>
                <a
                  href="#booking"
                  className="flex items-center justify-center gap-2 py-2.5 bg-red-600 text-white rounded-xl font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  Request Similar Beat
                </a>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-gray-900">${activeBeat.price}</span>
                {cartItemIds.has(activeBeat.id) ? (
                  <button
                    onClick={() => onRemoveFromCart(activeBeat.id)}
                    className="group flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-600 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                    title="Remove from cart"
                  >
                    <i className="ri-checkbox-circle-fill group-hover:hidden"></i>
                    <i className="ri-close-circle-fill hidden group-hover:block"></i>
                    <span className="group-hover:hidden">In Cart</span>
                    <span className="hidden group-hover:block">Remove</span>
                  </button>
                ) : (
                  <button
                    onClick={e => { e.stopPropagation(); onAddToCart(activeBeat); }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shopping-cart-2-line"></i>
                    Add to Cart
                  </button>
                )}
              </div>
            )}
          </AnimatedSection>

          {/* Right: Track List */}
          <AnimatedSection animation="slide-right" delay={350} className="flex-1 min-w-0 w-full">
            {/* Genre Filter */}
            <div className="flex items-center gap-2 mb-4 md:mb-5 overflow-x-auto no-scrollbar pb-1 flex-nowrap md:flex-wrap">
              {genres.map(g => (
                <button
                  key={g}
                  onClick={() => onGenreChange(g)}
                  className={`px-3 md:px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    activeGenre === g
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>

            {/* Track Rows */}
            <div className="relative">
              <div
                ref={trackListRef}
                onScroll={handleScroll}
                className="space-y-1 max-h-[800px] overflow-y-auto scroll-smooth pr-1"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#e5e7eb transparent' }}
              >
                {/* Column Headers */}
                <div className="hidden md:flex items-center gap-4 px-4 pb-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                  <span className="w-6 text-xs text-gray-400 text-center">#</span>
                  <span className="w-12 flex-shrink-0"></span>
                  <span className="flex-1 text-xs text-gray-400 font-medium uppercase tracking-wide">Title</span>
                  <span className="w-24 text-xs text-gray-400 font-medium uppercase tracking-wide hidden md:block">License</span>
                  <span className="w-20 text-xs text-gray-400 font-medium uppercase tracking-wide text-center hidden md:block">BPM / Key</span>
                  <span className="w-32 text-xs text-gray-400 font-medium uppercase tracking-wide text-right">Action</span>
                </div>

                {filteredBeats.map((beat, idx) => {
                  const isActive = activeBeat.id === beat.id;
                  const isCurrentlyPlaying = isActive && isPlaying;
                  const inCart = cartItemIds.has(beat.id);

                  return (
                    <div
                      key={beat.id}
                      onClick={() => onPlayBeat(beat)}
                      className={`flex items-center gap-2 md:gap-4 px-3 md:px-4 py-3 md:py-3.5 rounded-xl transition-all cursor-pointer group ${
                        isActive ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Index / Playing indicator */}
                      <div className="w-5 md:w-6 flex items-center justify-center flex-shrink-0">
                        {isCurrentlyPlaying ? (
                          <i className="ri-equalizer-line text-sm text-red-400 animate-pulse"></i>
                        ) : (
                          <>
                            <span className={`text-xs group-hover:hidden ${isActive ? 'text-gray-400' : 'text-gray-400'}`}>{idx + 1}</span>
                            <i className={`ri-play-fill text-sm hidden group-hover:block ${isActive ? 'text-white' : 'text-gray-700'}`}></i>
                          </>
                        )}
                      </div>

                      {/* Thumbnail */}
                      <div className="w-11 h-11 md:w-[52px] md:h-[52px] rounded-lg overflow-hidden flex-shrink-0 relative">
                        <img
                          src={beat.waveformImage}
                          alt={beat.title}
                          className="w-full h-full object-cover object-top"
                        />
                        {beat.isFree && (
                          <div className="absolute inset-0 flex items-center justify-center bg-green-500/80">
                            <span className="text-white text-xs font-bold">FREE</span>
                          </div>
                        )}
                        {beat.sold && !beat.isFree && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                            <span className="text-red-400 text-xs font-black uppercase tracking-wide">SOLD</span>
                          </div>
                        )}
                      </div>

                      {/* Title + genres */}
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold text-xs md:text-sm truncate ${isActive ? 'text-white' : 'text-gray-900'}`}>
                          {beat.title}
                        </div>
                        <div className="flex gap-1 flex-wrap mt-0.5">
                          {beat.genre.slice(0, 2).map(g => (
                            <span
                              key={g}
                              className={`text-xs px-1.5 py-0.5 rounded-full ${
                                isActive ? 'bg-white/20 text-gray-200' : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* License */}
                      <div className="w-24 hidden md:flex items-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          isActive ? 'bg-white/20 text-white' : 'bg-gray-900 text-white'
                        }`}>
                          {beat.licenseType}
                        </span>
                      </div>

                      {/* BPM / Key */}
                      <div className="w-20 text-center hidden md:block">
                        <div className={`text-xs font-medium ${isActive ? 'text-gray-300' : 'text-gray-600'}`}>
                          {beat.bpm} BPM
                        </div>
                        <div className={`text-xs ${isActive ? 'text-gray-400' : 'text-gray-400'}`}>
                          {beat.key}
                        </div>
                      </div>

                      {/* Action column */}
                      <div className="w-20 md:w-36 flex-shrink-0 flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                        {/* Share button */}
                        <div className="relative">
                          <button
                            onClick={e => { e.stopPropagation(); setSharePopupBeatId(sharePopupBeatId === beat.id ? null : beat.id); }}
                            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer flex-shrink-0 ${
                              isActive
                                ? 'text-gray-400 hover:text-white hover:bg-white/20'
                                : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                            title="Share"
                          >
                            <i className="ri-share-forward-line text-sm"></i>
                          </button>
                          {sharePopupBeatId === beat.id && (
                            <ShareBeatPopup
                              beat={beat}
                              onClose={() => setSharePopupBeatId(null)}
                            />
                          )}
                        </div>

                        {beat.isFree ? (
                          <button
                            onClick={() => handleFreeDownload(beat)}
                            className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
                              isActive
                                ? 'bg-white/20 text-white hover:bg-white/30'
                                : 'bg-gradient-to-r from-pink-500 to-red-600 text-white hover:opacity-90'
                            }`}
                          >
                            FREE
                          </button>
                        ) : beat.sold ? (
                          <div className="flex flex-col items-end gap-1">
                            <span className={`text-xs font-black uppercase tracking-wider ${isActive ? 'text-red-300' : 'text-red-500'}`}>
                              SOLD
                            </span>
                            <button
                              onClick={e => handleContactSimilar(beat, e)}
                              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                                isActive
                                  ? 'bg-white/15 text-white hover:bg-white/25'
                                  : 'bg-red-600 text-white hover:bg-red-700'
                              }`}
                            >
                              <i className="ri-mail-send-line text-xs"></i>
                              <span className="hidden md:inline">Contact</span>
                            </button>
                          </div>
                        ) : inCart ? (
                          <button
                            onClick={() => onRemoveFromCart(beat.id)}
                            title="Remove from cart"
                            className={`group flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                              isActive
                                ? 'bg-green-500/30 text-green-300 hover:bg-red-500/30 hover:text-red-300'
                                : 'bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-600'
                            }`}
                          >
                            <i className="ri-checkbox-circle-fill text-sm group-hover:hidden"></i>
                            <i className="ri-close-circle-fill text-sm hidden group-hover:block"></i>
                            <span className="hidden md:inline group-hover:hidden">In Cart</span>
                            <span className="hidden md:group-hover:inline">Remove</span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold hidden md:block ${isActive ? 'text-white' : 'text-gray-900'}`}>
                              ${beat.price}
                            </span>
                            <button
                              onClick={() => onAddToCart(beat)}
                              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                                isActive
                                  ? 'bg-white text-gray-900 hover:bg-gray-100'
                                  : 'bg-gray-900 text-white hover:bg-red-600'
                              }`}
                            >
                              <i className="ri-shopping-cart-2-line text-sm"></i>
                              <span className="hidden md:inline">Add</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom fade + scroll indicator */}
              {!isAtBottom && filteredBeats.length > 10 && (
                <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
                  <div className="h-20 bg-gradient-to-t from-white via-white/80 to-transparent rounded-b-xl" />
                </div>
              )}
              {!isAtBottom && filteredBeats.length > 10 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10">
                  <button
                    onClick={scrollDown}
                    className="w-8 h-8 flex items-center justify-center bg-gray-900 text-white rounded-full hover:bg-red-600 transition-colors cursor-pointer animate-bounce"
                    title="More beats below"
                  >
                    <i className="ri-arrow-down-s-line text-lg"></i>
                  </button>
                </div>
              )}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
